import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { PnidTemplate, MatchingMode, TemplateMatchResult, MatchStats } from '../types';
import { templateApi } from '../api';
import { TemplateEditor } from './TemplateEditor';

interface TemplatePanelProps {
  sourcePnidId: string;        // Current document annotation ID
  sourcePnidNo: string;        // Current P&ID number for display
  croppedImage: Blob | null;   // Triggers crop form view when not null
  onSaveComplete?: () => void; // Called after successful save
  onDiscardCrop?: () => void;  // Called when user discards crop
  // Template matching props
  onRunMatch?: (templateId: string, templateName: string, threshold: number, mode: MatchingMode) => Promise<any>;
  onSaveMatches?: (templateId: string, templateName: string) => Promise<any>;
  onDeleteAllMatches?: (templateId: string) => Promise<void>;
  matchStats?: Map<string, MatchStats>;
  unsavedMatches?: Map<string, TemplateMatchResult[]>;
  templateVisibility?: Map<string, boolean>;
  onToggleVisibility?: (templateId: string) => void;
  // Bulk identify props
  onIdentifyAll?: (templateId: string, templateName: string) => void;
  bulkIdentifyProgress?: Map<string, { total: number; completed: number; errors: number }>;
  savedAnnotationCounts?: Map<string, { unverified: number; total: number }>;
}

type PanelView = 'list' | 'crop-form' | 'editor';
type ListTab = 'local' | 'all';

const TemplateThumbnail: React.FC<{ template: PnidTemplate }> = ({ template }) => {
  const [imageUrl, setImageUrl] = useState('');

  useEffect(() => {
    let objectUrl: string | null = null;
    let cancelled = false;

    templateApi.resolveImageUrl(templateApi.getImageUrl(template.id))
      .then(({ url, revoke }) => {
        if (cancelled) {
          if (revoke) window.URL.revokeObjectURL(url);
          return;
        }
        objectUrl = revoke ? url : null;
        setImageUrl(url);
      })
      .catch((error) => {
        if (!cancelled) {
          console.warn('Could not load template thumbnail:', error);
          setImageUrl('');
        }
      });

    return () => {
      cancelled = true;
      if (objectUrl) window.URL.revokeObjectURL(objectUrl);
    };
  }, [template.id]);

  if (!imageUrl) return null;

  return (
    <img
      src={imageUrl}
      alt={template.name}
      style={{
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain',
      }}
      onError={(e) => {
        (e.target as HTMLImageElement).style.display = 'none';
      }}
    />
  );
};

const TemplatePanel: React.FC<TemplatePanelProps> = ({
  sourcePnidId,
  sourcePnidNo,
  croppedImage,
  onSaveComplete,
  onDiscardCrop,
  onRunMatch,
  onSaveMatches,
  onDeleteAllMatches,
  matchStats,
  unsavedMatches,
  templateVisibility,
  onToggleVisibility,
  onIdentifyAll,
  bulkIdentifyProgress,
  savedAnnotationCounts,
}) => {
  // Panel collapse state
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  // View state
  const [view, setView] = useState<PanelView>('list');
  const [listTab, setListTab] = useState<ListTab>('local');
  
  // Templates data
  const [templates, setTemplates] = useState<PnidTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Crop form state
  const [templateName, setTemplateName] = useState('');
  const [category, setCategory] = useState('');
  const [tags, setTags] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [cropPreviewUrl, setCropPreviewUrl] = useState<string | null>(null);
  
  // Editor state
  const [selectedTemplate, setSelectedTemplate] = useState<PnidTemplate | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  
  // Categories for dropdown
  const [categories, setCategories] = useState<string[]>([]);
  
  // Template matching state
  const [thresholds, setThresholds] = useState<Map<string, number>>(new Map());
  const [modes, setModes] = useState<Map<string, MatchingMode>>(new Map());
  const [matchingLoadingIds, setMatchingLoadingIds] = useState<Set<string>>(new Set());
  const [savingMatchesIds, setSavingMatchesIds] = useState<Set<string>>(new Set());
  const [deletingMatchesIds, setDeletingMatchesIds] = useState<Set<string>>(new Set());

  // Load templates
  const loadTemplates = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await templateApi.list(100, 0);
      setTemplates(response.results);
    } catch (err) {
      setError('Failed to load templates');
      console.error('Failed to load templates:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Load categories
  const loadCategories = useCallback(async () => {
    try {
      const response = await templateApi.getCategories();
      setCategories(response.categories);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadTemplates();
    loadCategories();
  }, [loadTemplates, loadCategories]);

  // Switch to crop form view when cropped image is received
  useEffect(() => {
    if (croppedImage) {
      setView('crop-form');
      setTemplateName('');
      setCategory('');
      setTags('');
      setSaveError(null);
      // Create preview URL
      const url = URL.createObjectURL(croppedImage);
      setCropPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      if (view === 'crop-form') {
        setView('list');
      }
      setCropPreviewUrl(null);
    }
  }, [croppedImage, view]);

  // Filter templates based on tab
  const filteredTemplates = useMemo(() => {
    if (listTab === 'local') {
      return templates.filter(t => t.source_pnid_id === sourcePnidId);
    }
    return templates;
  }, [templates, listTab, sourcePnidId]);

  // Handle save template
  const handleSaveTemplate = useCallback(async () => {
    if (!croppedImage || !templateName.trim()) return;
    
    setIsSaving(true);
    setSaveError(null);
    
    try {
      const tagList = tags
        .split(',')
        .map(t => t.trim())
        .filter(t => t.length > 0);
      
      await templateApi.create(
        croppedImage,
        templateName.trim(),
        sourcePnidId,
        category.trim() || undefined,
        tagList.length > 0 ? tagList : undefined
      );
      
      // Refresh templates list
      await loadTemplates();
      await loadCategories();
      
      // Clear form and go back to list
      setTemplateName('');
      setCategory('');
      setTags('');
      onSaveComplete?.();
    } catch (err) {
      setSaveError('Failed to save template');
      console.error('Failed to save template:', err);
    } finally {
      setIsSaving(false);
    }
  }, [croppedImage, templateName, category, tags, sourcePnidId, loadTemplates, loadCategories, onSaveComplete]);

  // Handle discard crop
  const handleDiscardCrop = useCallback(() => {
    setTemplateName('');
    setCategory('');
    setTags('');
    setSaveError(null);
    onDiscardCrop?.();
  }, [onDiscardCrop]);

  // Handle delete template
  const handleDeleteTemplate = useCallback(async () => {
    if (!selectedTemplate) return;
    
    setIsDeleting(true);
    try {
      await templateApi.delete(selectedTemplate.id);
      await loadTemplates();
      setSelectedTemplate(null);
      setView('list');
    } catch (err) {
      console.error('Failed to delete template:', err);
    } finally {
      setIsDeleting(false);
    }
  }, [selectedTemplate, loadTemplates]);

  // Handle template click
  const handleTemplateClick = useCallback((template: PnidTemplate) => {
    setSelectedTemplate(template);
    setView('editor');
  }, []);

  // Get threshold for a template (default 1.0)
  const getThreshold = useCallback((templateId: string) => {
    return thresholds.get(templateId) ?? 1.0;
  }, [thresholds]);

  // Set threshold for a template
  const setThreshold = useCallback((templateId: string, value: number) => {
    setThresholds(prev => {
      const next = new Map(prev);
      next.set(templateId, value);
      return next;
    });
  }, []);

  // Get matching mode for a template (default: tagged)
  const getMode = useCallback((templateId: string): MatchingMode => {
    return modes.get(templateId) ?? 'tagged';
  }, [modes]);

  // Set matching mode for a template
  const setMode = useCallback((templateId: string, value: MatchingMode) => {
    setModes(prev => {
      const next = new Map(prev);
      next.set(templateId, value);
      return next;
    });
  }, []);

  // Handle run match for a template
  const handleRunTemplateMatch = useCallback(async (template: PnidTemplate) => {
    if (!onRunMatch) return;
    
    setMatchingLoadingIds(prev => new Set(prev).add(template.id));
    try {
      await onRunMatch(template.id, template.name, getThreshold(template.id), getMode(template.id));
    } catch (err) {
      console.error('Failed to run template match:', err);
    } finally {
      setMatchingLoadingIds(prev => {
        const next = new Set(prev);
        next.delete(template.id);
        return next;
      });
    }
  }, [onRunMatch, getThreshold, getMode]);

  // Handle save matches for a template
  const handleSaveTemplateMatches = useCallback(async (template: PnidTemplate) => {
    if (!onSaveMatches) return;
    
    setSavingMatchesIds(prev => new Set(prev).add(template.id));
    try {
      await onSaveMatches(template.id, template.name);
    } catch (err) {
      console.error('Failed to save matches:', err);
    } finally {
      setSavingMatchesIds(prev => {
        const next = new Set(prev);
        next.delete(template.id);
        return next;
      });
    }
  }, [onSaveMatches]);

  // Handle delete all matches for a template
  const handleDeleteTemplateMatches = useCallback(async (template: PnidTemplate) => {
    if (!onDeleteAllMatches) return;
    
    setDeletingMatchesIds(prev => new Set(prev).add(template.id));
    try {
      await onDeleteAllMatches(template.id);
    } catch (err) {
      console.error('Failed to delete matches:', err);
    } finally {
      setDeletingMatchesIds(prev => {
        const next = new Set(prev);
        next.delete(template.id);
        return next;
      });
    }
  }, [onDeleteAllMatches]);

  // Render collapsed state
  if (isCollapsed) {
    return (
      <div
        style={{
          width: 48,
          height: '100%',
          backgroundColor: '#ffffff',
          borderLeft: '1px solid var(--raven-divider)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: 12,
        }}
      >
        <button
          onClick={() => setIsCollapsed(false)}
          style={{
            padding: '8px 6px',
            border: '1px solid var(--raven-divider)',
            borderRadius: 6,
            backgroundColor: 'var(--raven-surface-tier2)',
            cursor: 'pointer',
            writingMode: 'vertical-rl',
            textOrientation: 'mixed',
            fontSize: 12,
            fontWeight: 600,
            color: '#374151',
          }}
          title="Expand panel"
        >
          Templates
        </button>
      </div>
    );
  }

  // Render list view
  const renderListView = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 12px',
        borderBottom: '1px solid var(--raven-divider)',
      }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>Templates</span>
        <div style={{ display: 'flex', gap: 6 }}>
          <button
            onClick={loadTemplates}
            disabled={isLoading}
            style={{
              padding: '4px 8px',
              border: '1px solid var(--raven-divider)',
              borderRadius: 4,
              backgroundColor: '#ffffff',
              cursor: isLoading ? 'not-allowed' : 'pointer',
              fontSize: 12,
            }}
            title="Refresh"
          >
            {isLoading ? '...' : '↻'}
          </button>
          <button
            onClick={() => setIsCollapsed(true)}
            style={{
              padding: '4px 8px',
              border: '1px solid var(--raven-divider)',
              borderRadius: 4,
              backgroundColor: '#ffffff',
              cursor: 'pointer',
              fontSize: 12,
            }}
            title="Collapse panel"
          >
            »
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        display: 'flex',
        borderBottom: '1px solid var(--raven-divider)',
        backgroundColor: 'var(--raven-surface-tier2)',
      }}>
        <button
          onClick={() => setListTab('local')}
          style={{
            flex: 1,
            padding: '8px 12px',
            border: 'none',
            borderBottom: listTab === 'local' ? '2px solid var(--raven-primary-main)' : '2px solid transparent',
            backgroundColor: 'transparent',
            color: listTab === 'local' ? 'var(--raven-primary-main)' : '#6b7280',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Local ({templates.filter(t => t.source_pnid_id === sourcePnidId).length})
        </button>
        <button
          onClick={() => setListTab('all')}
          style={{
            flex: 1,
            padding: '8px 12px',
            border: 'none',
            borderBottom: listTab === 'all' ? '2px solid var(--raven-primary-main)' : '2px solid transparent',
            backgroundColor: 'transparent',
            color: listTab === 'all' ? 'var(--raven-primary-main)' : '#6b7280',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          All ({templates.length})
        </button>
      </div>

      {/* Template list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
        {listTab === 'all' && (
          <div
            style={{
              marginBottom: 10,
              padding: 14,
              borderRadius: 8,
              border: '1px solid #f59e0b',
              backgroundColor: '#fffbeb',
              boxShadow: '0 2px 8px rgba(245, 158, 11, 0.18)',
            }}
          >
            <div style={{ fontSize: 13, fontWeight: 700, color: '#92400e', marginBottom: 6 }}>
              "All Templates" is deprecated
            </div>
            <div style={{ fontSize: 12, lineHeight: 1.5, color: '#78350f' }}>
              Please use <strong>Local</strong> templates. For best matching quality, create
              new templates for each P&amp;ID instead of reusing templates across drawings.
            </div>
          </div>
        )}

        {listTab === 'all' ? null : (
          <>
            {error && (
              <div style={{
                padding: 12,
                backgroundColor: '#fef2f2',
                color: '#dc2626',
                borderRadius: 6,
                fontSize: 12,
                marginBottom: 8,
              }}>
                {error}
              </div>
            )}
            
            {filteredTemplates.length === 0 ? (
              <div style={{
                padding: 20,
                textAlign: 'center',
                color: '#9ca3af',
                fontSize: 12,
              }}>
                {listTab === 'local' 
                  ? 'No templates from this P&ID yet. Use the crop tool to create one.'
                  : 'No templates found.'}
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {filteredTemplates.map((template) => {
              const stats = matchStats?.get(template.id);
              const unsaved = unsavedMatches?.get(template.id) || [];
              const isVisible = templateVisibility?.get(template.id) !== false;
              const isMatching = matchingLoadingIds.has(template.id);
              const isSavingMatches = savingMatchesIds.has(template.id);
              const isDeletingMatches = deletingMatchesIds.has(template.id);
              const hasUnsaved = unsaved.length > 0;
              
              return (
                <div
                  key={template.id}
                  style={{
                    padding: 10,
                    borderRadius: 8,
                    border: '1px solid var(--raven-divider)',
                    backgroundColor: '#ffffff',
                  }}
                >
                  {/* Header row: Thumbnail + Info + Edit */}
                  <div style={{ display: 'flex', gap: 10, marginBottom: 8 }}>
                    {/* Thumbnail */}
                    <div style={{
                      width: 48,
                      height: 48,
                      borderRadius: 6,
                      backgroundColor: '#f3f4f6',
                      overflow: 'hidden',
                      flexShrink: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                      <TemplateThumbnail template={template} />
                    </div>
                    
                    {/* Info */}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: '#111827',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {template.name}
                      </div>
                      <div style={{
                        fontSize: 10,
                        color: '#6b7280',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}>
                        {template.source_pnid_no || 'Unknown P&ID'}
                      </div>
                      {template.category && (
                        <div style={{
                          display: 'inline-block',
                          fontSize: 9,
                          padding: '1px 5px',
                          backgroundColor: 'var(--raven-state-selected)',
                          color: 'var(--raven-primary-main)',
                          borderRadius: 3,
                          marginTop: 2,
                        }}>
                          {template.category}
                        </div>
                      )}
                    </div>
                    
                    {/* Edit button */}
                    <button
                      onClick={() => handleTemplateClick(template)}
                      style={{
                        alignSelf: 'flex-start',
                        padding: 5,
                        border: '1px solid var(--raven-divider)',
                        borderRadius: 4,
                        backgroundColor: '#ffffff',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                      title="Edit template"
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2">
                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                      </svg>
                    </button>
                  </div>
                  
                  {/* Matching controls */}
                  {onRunMatch && (
                    <>
                      {/* Mode toggle row */}
                      <div style={{ display: 'flex', marginBottom: 6, border: '1px solid var(--raven-divider)', borderRadius: 4, overflow: 'hidden' }}>
                        {(['untagged', 'tagged'] as MatchingMode[]).map((m) => {
                          const active = getMode(template.id) === m;
                          return (
                            <button
                              key={m}
                              onClick={() => setMode(template.id, m)}
                              style={{
                                flex: 1,
                                padding: '4px 0',
                                border: 'none',
                                backgroundColor: active ? '#3b82f6' : '#f3f4f6',
                                color: active ? '#ffffff' : '#374151',
                                fontSize: 11,
                                fontWeight: 600,
                                cursor: 'pointer',
                              }}
                            >
                            {m === 'tagged' ? 'NoText' : 'HasText'}
                            </button>
                          );
                        })}
                      </div>

                      {/* Threshold + Run Match row */}
                      <div style={{ display: 'flex', gap: 6, marginBottom: 6, alignItems: 'center' }}>
                        <label style={{ fontSize: 10, color: '#6b7280', whiteSpace: 'nowrap' }}>
                          Threshold:
                        </label>
                        <input
                          type="number"
                          min="0.1"
                          max="10"
                          step="0.1"
                          value={getThreshold(template.id)}
                          onChange={(e) => setThreshold(template.id, parseFloat(e.target.value) || 1.0)}
                          style={{
                            width: 50,
                            padding: '3px 5px',
                            border: '1px solid var(--raven-divider)',
                            borderRadius: 4,
                            fontSize: 11,
                            textAlign: 'center',
                          }}
                        />
                        <button
                          onClick={() => handleRunTemplateMatch(template)}
                          disabled={isMatching}
                          style={{
                            flex: 1,
                            padding: '5px 8px',
                            border: 'none',
                            borderRadius: 4,
                            backgroundColor: isMatching ? '#d1d5db' : '#3b82f6',
                            color: '#ffffff',
                            fontSize: 11,
                            fontWeight: 600,
                            cursor: isMatching ? 'not-allowed' : 'pointer',
                          }}
                        >
                          {isMatching ? 'Matching...' : 'Run Match'}
                        </button>
                      </div>
                      
                      {/* Stats row (if we have stats or matches) */}
                      {(stats || hasUnsaved) && (
                        <div style={{
                          padding: '6px 8px',
                          backgroundColor: 'var(--raven-surface-tier2)',
                          borderRadius: 4,
                          marginBottom: 6,
                          fontSize: 10,
                        }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                            <span style={{ color: '#6b7280' }}>Matches:</span>
                            <span style={{ fontWeight: 600, color: hasUnsaved ? '#f97316' : '#22c55e' }}>
                              {stats?.totalMatches ?? unsaved.length} {hasUnsaved ? '(unsaved)' : ''}
                            </span>
                          </div>
                          {stats && (
                            <>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                                <span style={{ color: '#6b7280' }}>Time:</span>
                                <span style={{ color: '#374151' }}>{stats.processingTime.toFixed(2)}s</span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 2 }}>
                                <span style={{ color: '#6b7280' }}>Avg Confidence:</span>
                                <span style={{ color: '#374151' }}>{(stats.avgConfidence * 100).toFixed(1)}%</span>
                              </div>
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: '#6b7280' }}>Avg Distance:</span>
                                <span style={{ color: '#374151' }}>{stats.avgChamferDistance.toFixed(3)}</span>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                      
                      {/* Identify All button / progress */}
                      {(() => {
                        const progress = bulkIdentifyProgress?.get(template.id);
                        const annotCounts = savedAnnotationCounts?.get(template.id);
                        const hasUnverified = annotCounts && annotCounts.unverified > 0;

                        if (progress) {
                          // Show progress bar
                          const pct = progress.total > 0 ? (progress.completed / progress.total) * 100 : 0;
                          return (
                            <div style={{ marginBottom: 6 }}>
                              <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                fontSize: 10,
                                color: '#6b7280',
                                marginBottom: 3,
                              }}>
                                <span>Identifying {progress.completed}/{progress.total}...</span>
                                {progress.errors > 0 && (
                                  <span style={{ color: '#dc2626' }}>{progress.errors} failed</span>
                                )}
                              </div>
                              <div style={{
                                height: 4,
                                backgroundColor: 'var(--raven-divider)',
                                borderRadius: 2,
                                overflow: 'hidden',
                              }}>
                                <div style={{
                                  height: '100%',
                                  width: `${pct}%`,
                                  backgroundColor: progress.errors > 0 ? '#f59e0b' : '#3b82f6',
                                  borderRadius: 2,
                                  transition: 'width 0.3s ease',
                                }} />
                              </div>
                            </div>
                          );
                        }

                        if (hasUnverified && onIdentifyAll && !hasUnsaved) {
                          return (
                            <button
                              onClick={() => onIdentifyAll(template.id, template.name)}
                              style={{
                                width: '100%',
                                padding: '6px 8px',
                                border: 'none',
                                borderRadius: 4,
                                backgroundColor: '#eab308',
                                color: '#ffffff',
                                fontSize: 11,
                                fontWeight: 600,
                                cursor: 'pointer',
                                marginBottom: 6,
                              }}
                            >
                              Identify All ({annotCounts.unverified})
                            </button>
                          );
                        }

                        return null;
                      })()}

                      {/* Action buttons row */}
                      <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                        {/* Save button (only if unsaved matches) */}
                        {hasUnsaved && onSaveMatches && (
                          <button
                            onClick={() => handleSaveTemplateMatches(template)}
                            disabled={isSavingMatches}
                            style={{
                              flex: 1,
                              padding: '5px 6px',
                              border: '1px solid #22c55e',
                              borderRadius: 4,
                              backgroundColor: isSavingMatches ? '#d1fae5' : '#ffffff',
                              color: '#22c55e',
                              fontSize: 10,
                              fontWeight: 600,
                              cursor: isSavingMatches ? 'not-allowed' : 'pointer',
                            }}
                          >
                            {isSavingMatches ? 'Saving...' : 'Save'}
                          </button>
                        )}
                        
                        {/* Delete all button */}
                        {(stats || hasUnsaved) && onDeleteAllMatches && (
                          <button
                            onClick={() => handleDeleteTemplateMatches(template)}
                            disabled={isDeletingMatches}
                            style={{
                              flex: 1,
                              padding: '5px 6px',
                              border: '1px solid #dc2626',
                              borderRadius: 4,
                              backgroundColor: isDeletingMatches ? '#fee2e2' : '#ffffff',
                              color: '#dc2626',
                              fontSize: 10,
                              fontWeight: 600,
                              cursor: isDeletingMatches ? 'not-allowed' : 'pointer',
                            }}
                          >
                            {isDeletingMatches ? 'Deleting...' : 'Delete All'}
                          </button>
                        )}
                        
                        {/* Visibility toggle */}
                        {onToggleVisibility && (
                          <button
                            onClick={() => onToggleVisibility(template.id)}
                            style={{
                              padding: '5px 8px',
                              border: '1px solid var(--raven-divider)',
                              borderRadius: 4,
                              backgroundColor: isVisible ? '#ffffff' : '#f3f4f6',
                              color: isVisible ? '#374151' : '#9ca3af',
                              fontSize: 12,
                              cursor: 'pointer',
                            }}
                            title={isVisible ? 'Hide annotations' : 'Show annotations'}
                          >
                            {isVisible ? '👁' : '👁‍🗨'}
                          </button>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
                })}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );

  // Render crop form view
  const renderCropFormView = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '10px 12px',
        borderBottom: '1px solid var(--raven-divider)',
      }}>
        <span style={{ fontSize: 14, fontWeight: 600, color: '#111827' }}>New Template</span>
        <button
          onClick={handleDiscardCrop}
          disabled={isSaving}
          style={{
            padding: '4px 8px',
            border: '1px solid var(--raven-divider)',
            borderRadius: 4,
            backgroundColor: '#ffffff',
            cursor: 'pointer',
            fontSize: 12,
          }}
          title="Discard"
        >
          ✕
        </button>
      </div>

      {/* Preview */}
      <div style={{
        flex: '0 0 auto',
        padding: 12,
        backgroundColor: 'var(--raven-surface-tier2)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        borderBottom: '1px solid var(--raven-divider)',
        maxHeight: 200,
        overflow: 'hidden',
      }}>
        {cropPreviewUrl && (
          <img
            src={cropPreviewUrl}
            alt="Cropped template"
            style={{
              maxWidth: '100%',
              maxHeight: 180,
              objectFit: 'contain',
              borderRadius: 6,
              border: '1px solid var(--raven-divider)',
              backgroundColor: '#ffffff',
            }}
          />
        )}
      </div>

      {/* Form */}
      <div style={{ flex: 1, padding: 12, overflowY: 'auto' }}>
        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>
            Template Name *
          </label>
          <input
            type="text"
            value={templateName}
            onChange={(e) => setTemplateName(e.target.value)}
            placeholder="e.g., Valve Type A"
            style={{
              width: '100%',
              padding: '8px 10px',
              border: '1px solid var(--raven-divider)',
              borderRadius: 6,
              fontSize: 13,
              outline: 'none',
            }}
            autoFocus
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>
            Category
          </label>
          <input
            type="text"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            placeholder="e.g., Valve, Transmitter"
            list="category-options"
            style={{
              width: '100%',
              padding: '8px 10px',
              border: '1px solid var(--raven-divider)',
              borderRadius: 6,
              fontSize: 13,
              outline: 'none',
            }}
          />
          <datalist id="category-options">
            {categories.map((cat) => (
              <option key={cat} value={cat} />
            ))}
          </datalist>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>
            Tags (comma-separated)
          </label>
          <input
            type="text"
            value={tags}
            onChange={(e) => setTags(e.target.value)}
            placeholder="e.g., gate, manual, 2-inch"
            style={{
              width: '100%',
              padding: '8px 10px',
              border: '1px solid var(--raven-divider)',
              borderRadius: 6,
              fontSize: 13,
              outline: 'none',
            }}
          />
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>
            Source P&ID
          </label>
          <div style={{ fontSize: 13, color: '#374151' }}>{sourcePnidNo}</div>
        </div>

        <div style={{ marginBottom: 12 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 4 }}>
            DPI
          </label>
          <div style={{ fontSize: 13, color: '#374151' }}>300</div>
        </div>

        {saveError && (
          <div style={{
            padding: 10,
            backgroundColor: '#fef2f2',
            color: '#dc2626',
            borderRadius: 6,
            fontSize: 12,
            marginBottom: 12,
          }}>
            {saveError}
          </div>
        )}
      </div>

      {/* Actions */}
      <div style={{
        display: 'flex',
        gap: 8,
        padding: 12,
        borderTop: '1px solid var(--raven-divider)',
      }}>
        <button
          onClick={handleDiscardCrop}
          disabled={isSaving}
          style={{
            flex: 1,
            padding: '10px 12px',
            border: '1px solid var(--raven-divider)',
            borderRadius: 6,
            backgroundColor: '#ffffff',
            color: '#374151',
            fontSize: 13,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Discard
        </button>
        <button
          onClick={handleSaveTemplate}
          disabled={isSaving || !templateName.trim()}
          style={{
            flex: 1,
            padding: '10px 12px',
            border: 'none',
            borderRadius: 6,
            backgroundColor: isSaving || !templateName.trim() ? '#d1d5db' : 'var(--raven-primary-main)',
            color: '#ffffff',
            fontSize: 13,
            fontWeight: 600,
            cursor: isSaving || !templateName.trim() ? 'not-allowed' : 'pointer',
          }}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );

  // Render editor view using TemplateEditor component
  const renderEditorView = () => {
    if (!selectedTemplate) return null;
    
    return (
      <TemplateEditor
        template={selectedTemplate}
        onSave={() => {
          loadTemplates();
          loadCategories();
        }}
        onBack={() => {
          setSelectedTemplate(null);
          setView('list');
        }}
        onDelete={handleDeleteTemplate}
        isDeleting={isDeleting}
      />
    );
  };

  return (
    <div
      style={{
        width: 320,
        height: '100%',
        backgroundColor: '#ffffff',
        borderLeft: '1px solid var(--raven-divider)',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {view === 'list' && renderListView()}
      {view === 'crop-form' && renderCropFormView()}
      {view === 'editor' && renderEditorView()}
    </div>
  );
};

export default TemplatePanel;
